<script setup>
import { useI18n } from 'vue-i18n';

import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Button from 'dashboard/components-next/button/Button.vue';

defineProps({
  teammate: { type: Object, required: true },
  memberships: { type: Array, default: () => [] },
  isCurrentUser: { type: Boolean, default: false },
  isRemoving: { type: Boolean, default: false },
});

const emit = defineEmits(['back', 'remove']);

const { t } = useI18n();

const ROW_CLASS = 'flex items-start gap-4 px-5 py-4';
const LABEL_CLASS = 'w-40 shrink-0 pt-1.5 text-sm font-medium text-n-slate-12';
</script>

<template>
  <div class="flex flex-col w-full gap-5 mx-auto max-w-5xl">
    <Button
      variant="ghost"
      color="slate"
      size="sm"
      icon="i-lucide-chevron-left"
      :label="t('TEAM_PAGE.TEAMMATE_DETAIL.BACK')"
      class="self-start"
      @click="emit('back')"
    />

    <header class="flex flex-wrap items-center gap-3">
      <Avatar
        :name="teammate.name"
        :src="teammate.thumbnail"
        :size="40"
        rounded-full
      />
      <div class="min-w-0">
        <h1
          class="m-0 text-xl font-semibold tracking-tight truncate text-n-slate-12"
        >
          {{ teammate.name }}
        </h1>
        <p class="mt-0.5 mb-0 text-sm truncate text-n-slate-11">
          {{ teammate.email }}
        </p>
      </div>
    </header>

    <div
      class="max-w-3xl overflow-hidden rounded-xl outline outline-1 -outline-offset-1 outline-n-container bg-n-solid-2"
    >
      <div class="divide-y divide-n-weak">
        <div :class="ROW_CLASS">
          <div :class="LABEL_CLASS">
            {{ t('TEAM_PAGE.TEAMMATE_DETAIL.EMAIL') }}
          </div>
          <p class="flex-1 min-w-0 pt-1.5 m-0 text-sm text-n-slate-11">
            {{ teammate.email }}
          </p>
        </div>
        <div :class="ROW_CLASS">
          <div :class="LABEL_CLASS">
            {{ t('TEAM_PAGE.TEAMMATE_DETAIL.ROLE') }}
          </div>
          <div class="flex-1 min-w-0 pt-1">
            <span
              class="inline-flex items-center h-6 px-2 text-xs font-medium rounded-md"
              :class="
                teammate.isAdministrator
                  ? 'bg-n-violet-3 text-n-violet-11'
                  : 'bg-n-alpha-2 text-n-slate-11'
              "
            >
              {{ teammate.roleLabel }}
            </span>
          </div>
        </div>
        <div :class="ROW_CLASS">
          <div :class="LABEL_CLASS">
            {{ t('TEAM_PAGE.TEAMMATE_DETAIL.STATUS') }}
          </div>
          <span
            class="flex items-center flex-1 min-w-0 gap-2 pt-1.5 text-sm text-n-slate-11"
          >
            <span
              class="rounded-full size-2 shrink-0"
              :class="teammate.statusDotClass"
            />
            {{ teammate.statusLabel }}
          </span>
        </div>
        <div :class="ROW_CLASS">
          <div :class="LABEL_CLASS">
            {{ t('TEAM_PAGE.TEAMMATE_DETAIL.TEAMS') }}
          </div>
          <div class="flex flex-wrap flex-1 min-w-0 gap-1.5 pt-1">
            <p v-if="!memberships.length" class="m-0 text-sm text-n-slate-10">
              {{ t('TEAM_PAGE.TEAMMATE_DETAIL.NO_TEAMS') }}
            </p>
            <span
              v-for="team in memberships"
              :key="team.id"
              class="inline-flex items-center h-6 px-2 text-xs font-medium rounded-md bg-n-alpha-2 text-n-slate-11"
            >
              {{ team.name }}
            </span>
          </div>
        </div>
      </div>
    </div>

    <Button
      variant="outline"
      color="ruby"
      :label="t('TEAM_PAGE.TEAMMATE_DETAIL.REMOVE')"
      :disabled="isCurrentUser || isRemoving"
      :is-loading="isRemoving"
      :title="
        isCurrentUser ? t('TEAM_PAGE.TEAMMATE_DETAIL.REMOVE_SELF') : undefined
      "
      class="self-start"
      @click="emit('remove', teammate.id)"
    />
  </div>
</template>
